<template>
  <div class="sale-category-container">
    <!-- 顶部操作栏 -->
    <div class="header-bar">
      <div class="left-section">
        <el-select
          v-model="selectedSaleOrgId"
          placeholder="请选择销售组织"
          clearable
          filterable
          @change="handleSaleOrgChange"
          style="width: 250px"
        >
          <el-option
            v-for="org in saleOrgList"
            :key="org.id"
            :label="org.name"
            :value="org.id"
          />
        </el-select>
      </div>
      <div class="right-section">
        <el-button type="primary" icon="el-icon-document-copy" @click="handleCopy" :disabled="!selectedSaleOrgId">复制</el-button>
        <el-button type="info" icon="el-icon-sort" @click="handleCompare">对比</el-button>
        <el-button type="warning" icon="el-icon-connection" @click="handleMerge" v-if="isAdmin">合并</el-button>
        <el-button type="success" icon="el-icon-time" @click="handleShowLogs" :disabled="!selectedSaleOrgId">操作日志</el-button>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 左侧分类树 -->
      <div class="left-panel">
        <div class="panel-header">
          <span class="panel-title">分类树</span>
          <div class="panel-actions">
            <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAddCategory(null)" :disabled="!selectedSaleOrgId">新增根分类</el-button>
          </div>
        </div>
        <div class="tree-search">
          <el-input
            v-model="treeFilterText"
            placeholder="输入关键字搜索"
            prefix-icon="el-icon-search"
            clearable
            size="small"
          />
        </div>
        <div class="tree-container" v-loading="treeLoading">
          <el-tree
            ref="categoryTree"
            :data="categoryTreeData"
            :props="treeProps"
            node-key="id"
            :filter-node-method="filterNode"
            :expand-on-click-node="false"
            highlight-current
            default-expand-all
            @node-click="handleNodeClick"
          >
            <span class="custom-tree-node" slot-scope="{ node, data }">
              <span class="node-label">
                <i :class="data.isLeaf ? 'el-icon-document' : 'el-icon-folder'"></i>
                {{ data.name }}
                <span class="node-number">({{ data.number }})</span>
              </span>
              <span class="node-actions">
                <el-button type="text" size="mini" icon="el-icon-plus" @click.stop="handleAddCategory(data)">新增</el-button>
                <el-button type="text" size="mini" icon="el-icon-edit" @click.stop="handleEditCategory(data)">编辑</el-button>
                <el-button type="text" size="mini" icon="el-icon-delete" class="delete-btn" @click.stop="handleDeleteCategory(data)">删除</el-button>
              </span>
            </span>
          </el-tree>
          <el-empty v-if="!treeLoading && categoryTreeData.length === 0" description="暂无分类数据"></el-empty>
        </div>
      </div>

      <!-- 右侧物料列表 -->
      <div class="right-panel">
        <div class="panel-header">
          <span class="panel-title">
            物料列表
            <span v-if="currentCategory" class="current-category">
              - {{ currentCategory.name }}
            </span>
          </span>
          <div class="panel-actions">
            <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleBindMaterial" :disabled="!currentCategory">绑定物料</el-button>
            <el-button type="danger" size="mini" icon="el-icon-delete" @click="handleUnbindMaterials" :disabled="selectedMaterials.length === 0">批量解绑</el-button>
          </div>
        </div>
        <div class="material-table" v-loading="materialLoading">
          <el-table
            :data="materialList"
            @selection-change="handleMaterialSelectionChange"
            border
            stripe
            height="calc(100% - 60px)"
          >
            <el-table-column type="selection" width="50" />
            <el-table-column prop="materialNumber" label="物料编码" width="150" />
            <el-table-column prop="materialName" label="物料名称" min-width="200" />
            <el-table-column prop="model" label="规格型号" width="120" />
            <el-table-column prop="sort" label="排序" width="80" />
            <el-table-column label="操作" width="100" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" size="mini" class="delete-btn" @click="handleUnbindMaterial(scope.row)">解绑</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            v-if="materialTotal > 0"
            @size-change="handleMaterialSizeChange"
            @current-change="handleMaterialPageChange"
            :current-page="materialPage"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="materialPageSize"
            layout="total, sizes, prev, pager, next"
            :total="materialTotal"
            style="margin-top: 10px; text-align: right"
          />
          <el-empty v-if="!materialLoading && materialList.length === 0 && currentCategory" description="该分类下暂无物料"></el-empty>
          <el-empty v-if="!currentCategory" description="请先选择一个分类"></el-empty>
        </div>
      </div>
    </div>

    <!-- 分类表单弹窗 -->
    <category-form
      ref="categoryForm"
      :visible.sync="categoryFormVisible"
      :form-data="categoryFormData"
      :parent-category="parentCategory"
      :sale-org-id="selectedSaleOrgId"
      @success="handleCategoryFormSuccess"
    />

    <!-- 物料绑定弹窗 -->
    <material-bind
      ref="materialBind"
      :visible.sync="materialBindVisible"
      :category="currentCategory"
      :sale-org-id="selectedSaleOrgId"
      @success="handleMaterialBindSuccess"
    />

    <!-- 对比弹窗 -->
    <category-compare
      ref="categoryCompare"
      :visible.sync="compareVisible"
      :sale-org-list="saleOrgList"
    />

    <!-- 合并弹窗 -->
    <category-merge
      ref="categoryMerge"
      :visible.sync="mergeVisible"
      :sale-org-list="saleOrgList"
      @success="handleMergeSuccess"
    />

    <!-- 复制弹窗 -->
    <el-dialog title="复制分类" :visible.sync="copyDialogVisible" width="500px" append-to-body>
      <el-form :model="copyForm" label-width="120px">
        <el-form-item label="源销售组织">
          <el-select v-model="copyForm.sourceSaleOrgId" placeholder="请选择源销售组织" filterable style="width: 100%">
            <el-option
              v-for="org in saleOrgList"
              :key="org.id"
              :label="org.name"
              :value="org.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="目标销售组织">
          <el-input :value="currentSaleOrgName" disabled />
        </el-form-item>
        <el-alert
          title="注意：复制操作会将源组织的分类树完整复制到目标组织，目标组织必须没有任何分类数据。"
          type="warning"
          :closable="false"
          show-icon
          style="margin-bottom: 10px"
        />
      </el-form>
      <div slot="footer">
        <el-button @click="copyDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmCopy" :loading="copyLoading">确认复制</el-button>
      </div>
    </el-dialog>

    <!-- 操作日志弹窗 -->
    <el-dialog title="操作日志" :visible.sync="logsDialogVisible" width="900px" append-to-body>
      <el-table :data="logsList" border stripe v-loading="logsLoading" max-height="400px">
        <el-table-column prop="operationType" label="操作类型" width="100">
          <template slot-scope="scope">
            <el-tag :type="getLogTypeTag(scope.row.operationType)">{{ getLogTypeName(scope.row.operationType) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="categoryNumber" label="分类编码" width="120" />
        <el-table-column prop="categoryName" label="分类名称" width="150" />
        <el-table-column prop="operationDesc" label="操作描述" min-width="200" />
        <el-table-column prop="createUserName" label="操作人" width="100" />
        <el-table-column prop="createTime" label="操作时间" width="160" />
      </el-table>
      <el-pagination
        v-if="logsTotal > 0"
        @current-change="handleLogsPageChange"
        :current-page="logsPage"
        :page-size="logsPageSize"
        layout="total, prev, pager, next"
        :total="logsTotal"
        style="margin-top: 10px; text-align: right"
      />
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  querySaleCategoryTree,
  deleteSaleCategory,
  copySaleCategory,
  querySaleCategoryLogs
} from '@/api/system/sale_category'
import { queryMaterialsByCategory, unbindMaterials } from '@/api/system/sale_category'
import { queryAllSaleOrgs, querySaleOrgChildren } from '@/api/system/org_sale'
import CategoryForm from './components/CategoryForm.vue'
import MaterialBind from './components/MaterialBind.vue'
import CategoryCompare from './components/CategoryCompare.vue'
import CategoryMerge from './components/CategoryMerge.vue'

export default {
  name: 'SaleCategoryIndex',
  components: {
    CategoryForm,
    MaterialBind,
    CategoryCompare,
    CategoryMerge
  },
  data() {
    return {
      // 销售组织
      saleOrgList: [],
      selectedSaleOrgId: '',

      // 分类树
      categoryTreeData: [],
      treeLoading: false,
      treeFilterText: '',
      treeProps: {
        children: 'children',
        label: 'name'
      },
      currentCategory: null,

      // 分类表单
      categoryFormVisible: false,
      categoryFormData: null,
      parentCategory: null,

      // 物料列表
      materialList: [],
      materialLoading: false,
      materialPage: 1,
      materialPageSize: 10,
      materialTotal: 0,
      selectedMaterials: [],

      // 物料绑定
      materialBindVisible: false,

      // 对比
      compareVisible: false,

      // 合并
      mergeVisible: false,

      // 复制
      copyDialogVisible: false,
      copyLoading: false,
      copyForm: {
        sourceSaleOrgId: ''
      },

      // 操作日志
      logsDialogVisible: false,
      logsLoading: false,
      logsList: [],
      logsPage: 1,
      logsPageSize: 10,
      logsTotal: 0
    }
  },
  computed: {
    ...mapGetters(['currentUser']),
    // 判断是否为超级管理员：检查 roles[0].roleKey === 'osp_admin'
    isAdmin() {
      return this.currentUser &&
             this.currentUser.roles &&
             this.currentUser.roles.length > 0 &&
             this.currentUser.roles[0].roleKey === 'osp_admin'
    },
    // 获取当前选中的销售组织（从 sessionStorage 读取，与 /user/company 页面同步）
    currentSaleOrg() {
      const saleOrgId = sessionStorage.getItem('saleOrgId')
      const saleOrgName = sessionStorage.getItem('saleOrgName')
      if (saleOrgId) {
        return { id: saleOrgId, name: saleOrgName }
      }
      return null
    },
    currentSaleOrgName() {
      const org = this.saleOrgList.find(o => o.id === this.selectedSaleOrgId)
      return org ? org.name : ''
    }
  },
  watch: {
    treeFilterText(val) {
      this.$refs.categoryTree && this.$refs.categoryTree.filter(val)
    }
  },
  mounted() {
    this.loadSaleOrgList()
  },
  methods: {
    // 加载销售组织列表
    async loadSaleOrgList() {
      try {
        if (this.isAdmin) {
          // 超级管理员获取所有销售组织
          const res = await queryAllSaleOrgs({})
          if (res.code === 200) {
            this.saleOrgList = res.data || []
          }
        } else {
          // 业务员：使用 sessionStorage 中的销售组织，获取其及其子组织
          if (this.currentSaleOrg) {
            const res = await querySaleOrgChildren({ parentId: this.currentSaleOrg.id })
            if (res.code === 200) {
              this.saleOrgList = res.data || []
            }
          }
        }
        // 默认选中第一个
        if (this.saleOrgList.length > 0) {
          this.selectedSaleOrgId = this.saleOrgList[0].id
          this.loadCategoryTree()
        }
      } catch (error) {
        console.error('加载销售组织失败:', error)
        this.$message.error('加载销售组织失败')
      }
    },

    // 销售组织变更
    handleSaleOrgChange() {
      this.currentCategory = null
      this.materialList = []
      this.materialTotal = 0
      if (this.selectedSaleOrgId) {
        this.loadCategoryTree()
      } else {
        this.categoryTreeData = []
      }
    },

    // 加载分类树
    async loadCategoryTree() {
      if (!this.selectedSaleOrgId) return

      this.treeLoading = true
      try {
        const res = await querySaleCategoryTree({ saleOrgId: this.selectedSaleOrgId })
        if (res.code === 200) {
          this.categoryTreeData = res.data || []
        }
      } catch (error) {
        console.error('加载分类树失败:', error)
        this.$message.error('加载分类树失败')
      } finally {
        this.treeLoading = false
      }
    },

    // 树节点过滤
    filterNode(value, data) {
      if (!value) return true
      return data.name.indexOf(value) !== -1 || data.number.indexOf(value) !== -1
    },

    // 点击树节点
    handleNodeClick(data) {
      this.currentCategory = data
      this.materialPage = 1
      this.loadMaterialList()
    },

    // 新增分类
    handleAddCategory(parentData) {
      this.categoryFormData = null
      this.parentCategory = parentData
      this.categoryFormVisible = true
    },

    // 编辑分类
    handleEditCategory(data) {
      this.categoryFormData = { ...data }
      this.parentCategory = null
      this.categoryFormVisible = true
    },

    // 删除分类
    handleDeleteCategory(data) {
      this.$confirm(`确定要删除分类"${data.name}"吗？如果该分类下有物料，将无法删除。`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await deleteSaleCategory({ ids: [data.id] })
          if (res.code === 200) {
            this.$message.success('删除成功')
            this.loadCategoryTree()
            if (this.currentCategory && this.currentCategory.id === data.id) {
              this.currentCategory = null
              this.materialList = []
            }
          } else {
            this.$message.error(res.msg || '删除失败')
          }
        } catch (error) {
          console.error('删除分类失败:', error)
          this.$message.error('删除分类失败')
        }
      }).catch(() => {})
    },

    // 分类表单提交成功
    handleCategoryFormSuccess() {
      this.loadCategoryTree()
    },

    // 加载物料列表
    async loadMaterialList() {
      if (!this.currentCategory) return

      this.materialLoading = true
      try {
        const res = await queryMaterialsByCategory({
          saleCategoryId: this.currentCategory.id,
          saleOrgId: this.selectedSaleOrgId,
          current: this.materialPage,
          size: this.materialPageSize
        })
        if (res.code === 200) {
          this.materialList = res.data.records || []
          this.materialTotal = res.data.total || 0
        }
      } catch (error) {
        console.error('加载物料列表失败:', error)
        this.$message.error('加载物料列表失败')
      } finally {
        this.materialLoading = false
      }
    },

    // 物料选择变更
    handleMaterialSelectionChange(selection) {
      this.selectedMaterials = selection
    },

    // 物料分页大小变更
    handleMaterialSizeChange(size) {
      this.materialPageSize = size
      this.materialPage = 1
      this.loadMaterialList()
    },

    // 物料页码变更
    handleMaterialPageChange(page) {
      this.materialPage = page
      this.loadMaterialList()
    },

    // 绑定物料
    handleBindMaterial() {
      this.materialBindVisible = true
    },

    // 物料绑定成功
    handleMaterialBindSuccess() {
      this.loadMaterialList()
    },

    // 解绑单个物料
    handleUnbindMaterial(row) {
      this.$confirm(`确定要解绑物料"${row.materialName}"吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await unbindMaterials({
            materialIds: [row.materialId],
            saleCategoryId: this.currentCategory.id
          })
          if (res.code === 200) {
            this.$message.success('解绑成功')
            this.loadMaterialList()
          } else {
            this.$message.error(res.msg || '解绑失败')
          }
        } catch (error) {
          console.error('解绑物料失败:', error)
          this.$message.error('解绑物料失败')
        }
      }).catch(() => {})
    },

    // 批量解绑物料
    handleUnbindMaterials() {
      this.$confirm(`确定要解绑选中的${this.selectedMaterials.length}个物料吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const materialIds = this.selectedMaterials.map(m => m.materialId)
          const res = await unbindMaterials({
            materialIds,
            saleCategoryId: this.currentCategory.id
          })
          if (res.code === 200) {
            this.$message.success('批量解绑成功')
            this.loadMaterialList()
          } else {
            this.$message.error(res.msg || '批量解绑失败')
          }
        } catch (error) {
          console.error('批量解绑物料失败:', error)
          this.$message.error('批量解绑物料失败')
        }
      }).catch(() => {})
    },

    // 复制分类
    handleCopy() {
      this.copyForm.sourceSaleOrgId = ''
      this.copyDialogVisible = true
    },

    // 确认复制
    async confirmCopy() {
      if (!this.copyForm.sourceSaleOrgId) {
        this.$message.warning('请选择源销售组织')
        return
      }
      if (this.copyForm.sourceSaleOrgId === this.selectedSaleOrgId) {
        this.$message.warning('源组织和目标组织不能相同')
        return
      }

      this.copyLoading = true
      try {
        const res = await copySaleCategory({
          sourceSaleOrgId: this.copyForm.sourceSaleOrgId,
          targetSaleOrgIds: [this.selectedSaleOrgId]
        })
        if (res.code === 200) {
          this.$message.success('复制成功')
          this.copyDialogVisible = false
          this.loadCategoryTree()
        } else {
          this.$message.error(res.msg || '复制失败')
        }
      } catch (error) {
        console.error('复制分类失败:', error)
        this.$message.error('复制分类失败')
      } finally {
        this.copyLoading = false
      }
    },

    // 对比分类
    handleCompare() {
      this.compareVisible = true
    },

    // 合并分类
    handleMerge() {
      this.mergeVisible = true
    },

    // 合并成功
    handleMergeSuccess() {
      this.loadCategoryTree()
    },

    // 显示操作日志
    handleShowLogs() {
      this.logsPage = 1
      this.loadLogs()
      this.logsDialogVisible = true
    },

    // 加载操作日志
    async loadLogs() {
      this.logsLoading = true
      try {
        const res = await querySaleCategoryLogs({
          saleOrgId: this.selectedSaleOrgId,
          current: this.logsPage,
          size: this.logsPageSize
        })
        if (res.code === 200) {
          this.logsList = res.data.records || []
          this.logsTotal = res.data.total || 0
        }
      } catch (error) {
        console.error('加载操作日志失败:', error)
        this.$message.error('加载操作日志失败')
      } finally {
        this.logsLoading = false
      }
    },

    // 日志页码变更
    handleLogsPageChange(page) {
      this.logsPage = page
      this.loadLogs()
    },

    // 获取日志类型标签
    getLogTypeTag(type) {
      const map = {
        'CREATE': 'success',
        'UPDATE': 'primary',
        'DELETE': 'danger',
        'COPY': 'info',
        'MERGE': 'warning'
      }
      return map[type] || 'info'
    },

    // 获取日志类型名称
    getLogTypeName(type) {
      const map = {
        'CREATE': '新增',
        'UPDATE': '修改',
        'DELETE': '删除',
        'COPY': '复制',
        'MERGE': '合并'
      }
      return map[type] || type
    }
  }
}
</script>

<style scoped>
.sale-category-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 15px;
  background-color: #f5f7fa;
}

.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background-color: #fff;
  border-radius: 4px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.main-content {
  flex: 1;
  display: flex;
  gap: 15px;
  min-height: 0;
}

.left-panel {
  width: 400px;
  background-color: #fff;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.right-panel {
  flex: 1;
  min-width: 0;
  background-color: #fff;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #ebeef5;
}

.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.current-category {
  color: #409eff;
  font-weight: normal;
}

.tree-search {
  padding: 10px 15px;
  border-bottom: 1px solid #ebeef5;
}

.tree-container {
  flex: 1;
  overflow: auto;
  padding: 10px;
}

.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  padding-right: 8px;
}

.node-label {
  display: flex;
  align-items: center;
  gap: 5px;
}

.node-label i {
  color: #909399;
}

.node-number {
  color: #909399;
  font-size: 12px;
}

.node-actions {
  visibility: hidden;
  white-space: nowrap;
}

.custom-tree-node:hover .node-actions {
  visibility: visible;
}

.delete-btn {
  color: #f56c6c;
}

.material-table {
  flex: 1;
  padding: 15px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>
